import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@material-ui/core";
import { toast } from "react-toastify";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import { updateVariantTags } from "views/Inputs/viewServices/apiCalls";
import VariantTagCard from "./VariantTagCard";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  title: {
    fontWeight: 500,
    fontSize: "1rem",
    marginBottom: theme.spacing(2),
    color: grayColor[2],
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
  },
  emptyState: {
    color: grayColor[4],
    fontStyle: "italic",
    padding: theme.spacing(4),
    textAlign: "center",
  },
}));

const TabVariantManagement = ({
  selectedInputTable,
  variants: initialVariants,
  onVariantsChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });

  // Local state for optimistic updates
  const [variants, setVariants] = useState(initialVariants);

  // Update local state when props change
  React.useEffect(() => {
    setVariants(initialVariants);
  }, [initialVariants]);

  // Compute available tags from all variants in this table
  const availableTags = useMemo(() => {
    const allTags = new Set();
    variants.forEach((v) => {
      if (v.tags) {
        v.tags.forEach((tag) => allTags.add(tag));
      }
    });
    return Array.from(allTags).sort();
  }, [variants]);

  // Optimistic update helper
  const updateVariantLocally = useCallback((variantId, newTags) => {
    setVariants((prev) =>
      prev.map((v) =>
        v.variant_id === variantId ? { ...v, tags: newTags } : v,
      ),
    );
  }, []);

  // Add tag to variant
  const handleAddTag = useCallback(
    async (variantId, tagName) => {
      const variant = variants.find((v) => v.variant_id === variantId);
      if (!variant) return;

      const currentTags = variant.tags || [];
      if (currentTags.includes(tagName)) return;

      const newTags = [...currentTags, tagName];

      // Optimistic update
      updateVariantLocally(variantId, newTags);

      try {
        await updateVariantTags(selectedInputTable, variantId, newTags);
        toast.success(t("tag_added"));

        // Notify parent of change
        if (onVariantsChange) {
          onVariantsChange();
        }
      } catch (error) {
        // Rollback on error
        updateVariantLocally(variantId, currentTags);
        toast.error(error.response?.data?.error || t("error_adding_tag"));
      }
    },
    [variants, selectedInputTable, t, updateVariantLocally, onVariantsChange],
  );

  // Remove tag from variant
  const handleRemoveTag = useCallback(
    async (variantId, tagName) => {
      const variant = variants.find((v) => v.variant_id === variantId);
      if (!variant) return;

      const currentTags = variant.tags || [];
      const newTags = currentTags.filter((t) => t !== tagName);

      // Optimistic update
      updateVariantLocally(variantId, newTags);

      try {
        await updateVariantTags(selectedInputTable, variantId, newTags);
        toast.success(t("tag_removed"));

        // Notify parent of change
        if (onVariantsChange) {
          onVariantsChange();
        }
      } catch (error) {
        // Rollback on error
        updateVariantLocally(variantId, currentTags);
        toast.error(error.response?.data?.error || t("error_removing_tag"));
      }
    },
    [variants, selectedInputTable, t, updateVariantLocally, onVariantsChange],
  );

  if (!variants || variants.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.emptyState}>
          {t("no_variants_available")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>
        {t("variant_management")}
      </Typography>
      <Box className={classes.grid}>
        {variants.map((variant) => (
          <VariantTagCard
            key={variant.variant_id}
            variant={variant}
            availableTags={availableTags}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
          />
        ))}
      </Box>
    </Box>
  );
};

TabVariantManagement.propTypes = {
  selectedInputTable: PropTypes.string.isRequired,
  variants: PropTypes.array.isRequired,
  onVariantsChange: PropTypes.func,
};

export default TabVariantManagement;
